/**
 * wordOpsLogger — ring buffer + pub-sub of every Word.run firing in
 * the add-in, plus a ``withWordOpLog`` helper that wraps a Word.run-
 * using function with auto-timing.
 *
 * # Why this exists
 *
 * Word's Office.js bridge is the dominant performance cost in the
 * add-in. Until now there was no way to *see* when a Word.run fired,
 * how long it took, or which selection / cursor change caused it.
 * Hangs on long CSR drafts were diagnosed by guessing. This module
 * surfaces every bridge round-trip so they can be inspected live.
 *
 * # Mechanism
 *
 *   1. ``logWordOp(entry)`` pushes into a ring buffer (last
 *      ``BUFFER_SIZE`` entries) and notifies all subscribers.
 *   2. ``withWordOpLog(name, fn, detailsFromResult?)`` wraps a Word
 *      operation: starts a perf timer, awaits the work, computes
 *      duration, optionally extracts details from the result, then
 *      ``logWordOp``s the entry. Exceptions are caught, logged with
 *      ``error: true``, and re-thrown.
 *   3. ``WordOpsPanel.tsx`` subscribes and renders the ring buffer
 *      when ``localStorage["asthra:debug:word-ops"] === "1"``.
 *
 * Everything in this module is pure JS — no Office.js dependency, no
 * React. Tests can drive it without a Word host.
 */

const BUFFER_SIZE = 200;
const CONSOLE_FLAG = "asthra:debug:word-ops";

export interface WordOpLogEntry {
  /** Epoch milliseconds at completion (or error). */
  ts: number;
  /** Stable name for the op, e.g. "checkDocEmpty", "readSelectionText". */
  name: string;
  /** Wall-clock duration in ms, rounded to two decimals. */
  durationMs: number;
  /** Free-form structured info — e.g. ``{ isEmpty: true }`` or
   *  ``{ branch: "multi-paragraph-short-circuit", paragraphs: 5273 }``. */
  details?: Record<string, unknown>;
  /** ``true`` when the wrapped op threw. ``details.error`` carries the message. */
  error?: boolean;
}

type Listener = (entries: readonly WordOpLogEntry[]) => void;

const buffer: WordOpLogEntry[] = [];
const listeners = new Set<Listener>();

function shouldConsoleLog(): boolean {
  try {
    return globalThis.window !== undefined && globalThis.window.localStorage?.getItem(CONSOLE_FLAG) === "1";
  } catch {
    return false;
  }
}

function notify(): void {
  const snapshot = buffer.slice();
  for (const l of Array.from(listeners)) {
    try {
      l(snapshot);
    } catch (err) {
      console.warn("[wordOpsLogger] listener threw:", err);
    }
  }
}

function shouldAlwaysConsoleLog(entry: WordOpLogEntry): boolean {
  return entry.name.startsWith("chipVisibility:");
}

/** Push an entry into the ring buffer and notify subscribers. */
export function logWordOp(entry: WordOpLogEntry): void {
  buffer.push(entry);
  if (buffer.length > BUFFER_SIZE) buffer.shift();
  if (shouldAlwaysConsoleLog(entry) || shouldConsoleLog()) {
    // eslint-disable-next-line no-console
    console.info(shouldAlwaysConsoleLog(entry) ? "[chip-debug]" : "[word-op]", entry);
  }
  notify();
}

/** Log a zero-duration debug step into the same ring buffer/console path
 *  as Word op timings so event lifecycles can be inspected alongside
 *  Word.run entries. */
export function logWordDebugStep(name: string, details?: Record<string, unknown>): void {
  logWordOp({
    ts: Date.now(),
    name,
    durationMs: 0,
    details,
  });
}

/** Subscribe to the ring buffer. Returns the unsubscribe function.
 *  The listener is called once immediately with the current snapshot. */
export function subscribeWordOps(cb: Listener): () => void {
  listeners.add(cb);
  try {
    cb(buffer.slice());
  } catch (err) {
    console.warn("[wordOpsLogger] initial listener call threw:", err);
  }
  return () => {
    listeners.delete(cb);
  };
}

/** Read-only snapshot of the current ring buffer. */
export function getWordOps(): readonly WordOpLogEntry[] {
  return buffer.slice();
}

/** Clear the ring buffer. Notifies subscribers. */
export function clearWordOps(): void {
  buffer.length = 0;
  notify();
}

/** Test-only — reset module state. */
export function _resetWordOpsForTesting(): void {
  buffer.length = 0;
  listeners.clear();
}

/** Monotonic high-resolution clock in ms (falls back to `Date.now()` where
 *  `performance` is unavailable). Exported so other modules (e.g. the sync
 *  engine's per-phase timing) share one guarded implementation. */
export function nowMs(): number {
  try {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
  } catch {
    // fall through
  }
  return Date.now();
}

function now(): number {
  return nowMs();
}

/**
 * Wrap a Word-touching async function with auto-timing and auto-logging.
 *
 * @param name           Stable identifier shown in the panel.
 * @param fn             The work to perform — typically a function that
 *                       runs ``Word.run(...)`` internally.
 * @param detailsFromResult Optional projector from the result into the
 *                       structured ``details`` field. Useful for
 *                       surfacing branch information (``{ branch:
 *                       "isEmpty" }``) or scalar results (``{ isEmpty:
 *                       true }``).
 *
 * Errors are caught, logged with ``error: true``, and re-thrown so
 * callers can apply their own fallbacks.
 */
export async function withWordOpLog<T>(
  name: string,
  fn: () => Promise<T>,
  detailsFromResult?: (result: T) => Record<string, unknown>,
): Promise<T> {
  const start = now();
  try {
    const result = await fn();
    const durationMs = Math.round((now() - start) * 100) / 100;
    const details = detailsFromResult === undefined ? undefined : safeDetails(detailsFromResult, result);
    logWordOp({ ts: Date.now(), name, durationMs, details });
    return result;
  } catch (err) {
    const durationMs = Math.round((now() - start) * 100) / 100;
    logWordOp({
      ts: Date.now(),
      name,
      durationMs,
      error: true,
      details: { error: err instanceof Error ? err.message : String(err) },
    });
    throw err;
  }
}

function safeDetails<T>(fn: (result: T) => Record<string, unknown>, result: T): Record<string, unknown> | undefined {
  try {
    return fn(result);
  } catch (err) {
    return { detailsExtractionFailed: String(err) };
  }
}
